import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import LockedPhotoCard from "@/components/LockedPhotoCard";
import UnlockPaymentModal from "@/components/UnlockPaymentModal";
import SafetyActions from "@/components/SafetyActions";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Wallet, ImageOff, Lock } from "lucide-react";
import { euro } from "@/lib/format";
import { toast } from "sonner";

const Home = () => {
  const { user, setUser, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/photos/received");
      setPhotos(data.photos);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openUnlock = (photo) => {
    setSelected(photo);
    setModalOpen(true);
    api.post(`/photos/${photo.id}/view`).catch(() => {});
  };

  const confirmUnlock = async (photo) => {
    try {
      const { data } = await api.post(`/photos/${photo.id}/purchase`);
      setUser(data.user);
      toast.success("Photo déverrouillée — profitez-en !");
      await load();
    } catch (e) {
      toast.error(apiError(e));
      throw e;
    }
  };

  return (
    <div className="space-y-5">
      {/* Balance banner */}
      <div className="rounded-2xl bg-card border border-border p-4 flex items-center justify-between shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)]">
        <div>
          <p className="text-xs text-muted-foreground">{t("home.yourBalance")}</p>
          <p className="font-display text-2xl" data-testid="home-balance">{euro(user?.wallet_balance)}</p>
        </div>
        <Button variant="outline" className="rounded-xl" onClick={() => navigate("/portefeuille")} data-testid="home-recharge">
          <Wallet className="h-4 w-4 mr-1" /> {t("home.recharge")}
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg">{t("home.received")}</h2>
        <span className="text-xs text-muted-foreground">{photos.length} {t("home.count")}</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3">
            <ImageOff className="h-6 w-6" />
          </div>
          <p className="font-medium text-foreground">{t("home.noPhotos")}</p>
          <p className="text-sm mt-1">{t("home.noPhotosHint")}</p>
          <Button className="mt-4 rounded-xl" onClick={() => navigate("/amis")} data-testid="home-add-friends">{t("home.findFriends")}</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative">
              <LockedPhotoCard photo={p} onUnlock={openUnlock} />
              <div className="absolute top-2 right-2">
                <SafetyActions targetUser={p.sender} photo={p} onBlocked={load} />
              </div>
            </div>
          ))}
        </div>
      )}

      <UnlockPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        photo={selected}
        balance={user?.wallet_balance || 0}
        onConfirm={confirmUnlock}
        onRecharge={() => navigate("/portefeuille")}
      />
    </div>
  );
};

export default Home;
